class SwiperComponent extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
  }

  init() {
    if (typeof Swiper === 'undefined') return;

    const container = this.querySelector('.swiper');
    if (!container) return;

    // centeredSlides keeps the active slide centered at every position,
    // with a symmetric sliver of the previous/next real slide (or loop
    // clone) showing on each side automatically — the right way to get a
    // peek that holds up while looping through every slide, not just the
    // first one. The alternative "peek slide" hack below (a duplicated last
    // slide prepended with a manual slidesOffsetBefore) only ever lines up
    // at the initial position: once loop clones shift the DOM around after
    // navigating, that hand-placed offset stops matching reality.
    const centered = this.dataset.centered === 'true';
    // Optional: let a section keep centered peeking on mobile/tablet but show
    // the first slide flush (uncentered) on desktop, or vice versa. Falls
    // back to the same value as `centered` when not set, so sections that
    // don't need a different desktop behavior are unaffected.
    const centeredDesktop =
      this.dataset.centeredDesktop === undefined ? centered : this.dataset.centeredDesktop === 'true';

    // A "peek" slide (a real, pre-rendered duplicate of the last slide) may
    // be prepended so the peek always has real content instead of depending
    // on Swiper's loop-clone timing. Swiper positions slides purely by index
    // using a uniform computed width, so a slide merely hidden with CSS would
    // still occupy a slot and leave a gap where it used to be — remove it
    // from the DOM before Swiper measures anything if this instance doesn't
    // want a peek at the current breakpoint.
    const peekOnMobile = this.dataset.peekMobile === 'true';
    const isDesktop = window.innerWidth >= 990;
    const peekSlide = this.querySelector('[data-peek-slide]');
    if (peekSlide && (centered || (!isDesktop && !peekOnMobile))) {
      peekSlide.remove();
    }
    const hasPeekSlide = !centered && !!this.querySelector('[data-peek-slide]');

    // When a breakpoint isn't centered but loop IS on, Swiper has already
    // generated its own real-content clone of the last slide just before
    // index 0 for seamless wraparound — no manual peek slide needed. A
    // non-zero slidesOffsetBefore there simply reveals a sliver of that
    // clone, and unlike the manual peek-slide hack above, it stays correctly
    // aligned at every position since Swiper (not this component) owns and
    // repositions those clones as navigation happens.
    const loopEnabled = this.dataset.loop === 'true';
    const wantsBasePeek = hasPeekSlide || (!centered && loopEnabled);
    const wantsDesktopPeek = hasPeekSlide || (!centeredDesktop && loopEnabled);

    // Nav buttons usually live inside the component, but data-nav-root lets a
    // section place them elsewhere in the markup (e.g. in a header, above the
    // slider) while still wiring them up to this same swiper instance.
    const navRoot = (this.dataset.navRoot && document.querySelector(this.dataset.navRoot)) || this;
    const nextEl = navRoot.querySelector('.swiper-button-next');
    const prevEl = navRoot.querySelector('.swiper-button-prev');
    const paginationEl = this.querySelector('.swiper-pagination');

    // Attached before the Swiper instance exists, so — since both listeners
    // sit on the same button and phase — this one runs first on click,
    // ahead of Swiper's own navigation handler: a resize, an image finishing
    // its load, or a dynamically-changed slide can leave Swiper's cached
    // slide measurements stale, and slideNext/slidePrev would then move to
    // the wrong offset using those stale numbers. update() re-measures
    // immediately before the move actually happens.
    //
    // Skipped while a transition is still in flight (this.swiper.animating):
    // update() ends with an internal slideTo(activeIndex, 0, false, true)
    // that, at speed 0, resolves synchronously through transitionEnd() and
    // force-clears `animating` — even though the browser is still mid-flight
    // animating the PREVIOUS click's CSS transition. That defeats Swiper's
    // own loopPreventsSliding guard (which only skips a new slidePrev/
    // slideNext while animating is true), so a fast run of clicks on a loop
    // slider let a new loopFix() DOM reorder land on top of a transition
    // that hadn't visually finished yet — seen as a blank-slide flash after
    // several rapid clicks on whichever side loopFix's clone-recycling
    // boundary gets crossed almost every click (the flush/uncentered side of
    // a peek layout). Only skipping the *stale-measurement* re-sync here,
    // not the click itself, keeps the original fix intact for the normal
    // (not-mid-transition) case.
    const updateBeforeMove = () => {
      if (!this.swiper || this.swiper.animating) return;
      this.swiper.update();
    };
    nextEl?.addEventListener('click', updateBeforeMove);
    prevEl?.addEventListener('click', updateBeforeMove);
    const speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    // `|| fallback` would also catch an intentional 0 (e.g. a whole-number
    // "items per page" that should show no peek at all) since 0 is falsy —
    // only fall back when the attribute is genuinely missing/unparseable.
    const parseOffset = (value, fallback) => {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    };
    const offsetDesktop = parseOffset(this.dataset.offsetBeforeDesktop, 140);
    // Only used when peekOnMobile is set — lets the peek be a smaller width
    // on narrow screens than the one configured for desktop.
    const offsetMobile = parseOffset(this.dataset.offsetBeforeMobile, offsetDesktop);
    // Opt-in: extra loop clones kept pre-built around the current slide at
    // the desktop breakpoint specifically, so a real DOM slide (with its
    // image already loaded, since the section renders these without
    // data-src lazy loading) is always sitting a few positions ahead of
    // where loopFix's clone-recycling boundary gets crossed, instead of
    // that boundary needing to fire and swap content in right as it comes
    // into view. Deliberately NOT applied to the base/mobile config (see the
    // loopAdditionalSlides comment below on why raising it globally
    // regressed mobile drag) — only set where a section opts in via this
    // attribute, so sliders that don't set it are unaffected.
    const loopAdditionalSlidesDesktop = parseInt(this.dataset.loopAdditionalSlidesDesktop, 10);

    // A plain, always-on edge gutter — unlike offsetMobile/offsetDesktop
    // above (which only ever apply through the loop-clone/peek-slide
    // machinery via wantsBasePeek/wantsDesktopPeek), these apply regardless
    // of loop/centered/peek state. Swiper reserves this space only once, at
    // the very start and end of the whole slide track, so it's visible on
    // the first/last slide only — not a continuous peek repeated at every
    // scroll position the way slide-width-based padding would be.
    const edgeOffsetBeforeMobile = parseOffset(this.dataset.edgeOffsetBefore, 0);
    const edgeOffsetAfterMobile = parseOffset(this.dataset.edgeOffsetAfter, 0);
    const edgeOffsetBeforeDesktop = parseOffset(this.dataset.edgeOffsetBeforeDesktop, 0);
    const edgeOffsetAfterDesktop = parseOffset(this.dataset.edgeOffsetAfterDesktop, 0);
    // Optional: show fewer cards between 990-1199px than at full desktop
    // width (≥1200px). Only added when a section opts in via this attribute,
    // so sliders that don't set it keep their existing single 990 breakpoint.
    const slidesDesktopSmall = parseFloat(this.dataset.slidesDesktopSmall);

    // The peek slide sits at real slide index 0. To reveal only a sliver of
    // it (rather than showing it fully or hiding it completely), the active
    // slide needs to start at index 1 (the real first card) AND the track
    // needs a small offset before it — that offset is exactly wide enough to
    // show the tail end of slide 0 sitting immediately before it.
    // "auto" sizes each slide to its own content (e.g. a row of variable-width
    // tab buttons) instead of Swiper dividing the container into N equal
    // slides — a numeric slidesPerView forces every slide to that computed
    // width via inline style, which clips or overlaps content sized by CSS.
    const parseSlidesPerView = (value, fallback) => (value === 'auto' ? 'auto' : parseFloat(value) || fallback);
    const mobileSlidesPerView = parseSlidesPerView(this.dataset.slidesMobile, 1.15);
    const desktopSlidesPerView = parseSlidesPerView(this.dataset.slidesDesktop, 3.2);

    // For the native-loop-clone peek (wantsBasePeek/wantsDesktopPeek without
    // a manual peek slide) to show on BOTH sides while the first slide still
    // sits flush (not centered), Swiper needs a slightly NARROWER slide than
    // the raw "items per page" value implies — narrow enough that the same
    // fixed viewport can fit one peek sliver + the whole slides + a second
    // matching peek sliver, all at once. Solving slideWidth * (floor(raw) +
    // 2 * fraction) = viewport width for slideWidth gives an adjusted
    // slidesPerView of floor(raw) + 2*fraction (instead of the raw
    // floor(raw) + fraction), with the peek itself sized to
    // fraction * (viewport / adjustedSlidesPerView). A raw whole number
    // (fraction 0) needs no adjustment — it already has no peek to mirror.
    const computeSymmetricPeek = (rawSlidesPerView) => {
      if (rawSlidesPerView === 'auto' || !rawSlidesPerView) {
        return { slidesPerView: rawSlidesPerView, offset: 0 };
      }
      const floorSlides = Math.floor(rawSlidesPerView);
      const fraction = rawSlidesPerView - floorSlides;
      if (fraction <= 0) {
        return { slidesPerView: rawSlidesPerView, offset: 0 };
      }
      const adjustedSlidesPerView = floorSlides + fraction * 2;
      const offset = Math.round(fraction * (container.clientWidth / adjustedSlidesPerView));
      return { slidesPerView: adjustedSlidesPerView, offset };
    };
    const mobilePeek = wantsBasePeek
      ? computeSymmetricPeek(mobileSlidesPerView)
      : { slidesPerView: mobileSlidesPerView, offset: 0 };
    const desktopPeek = wantsDesktopPeek
      ? computeSymmetricPeek(desktopSlidesPerView)
      : { slidesPerView: desktopSlidesPerView, offset: 0 };

    this.swiper = new Swiper(container, {
      slidesPerView: mobilePeek.slidesPerView,
      // Without this, a single swipe/flick can advance by more than one
      // slide when its drag distance covers more than one slide-width —
      // which happens easily on a high-slidesPerView, stacked-card layout
      // where each slide is quite narrow. Locking this to 1 means a swipe
      // always moves exactly one slide index, regardless of how far (or
      // how fast) the drag itself covered.
      slidesPerGroup: 1,
      // Opt-in: a section can disable touch/drag swiping entirely (leaving
      // the nav arrows as the only way to navigate) without affecting any
      // other section using this same component — defaults to swipe
      // staying enabled, so sections that don't set this are unaffected.
      allowTouchMove: this.dataset.disableSwipe !== 'true',
      centeredSlides: centered,
      slidesOffsetBefore: (wantsBasePeek ? (hasPeekSlide ? offsetMobile : mobilePeek.offset) : 0) + edgeOffsetBeforeMobile,
      slidesOffsetAfter: edgeOffsetAfterMobile,
      initialSlide: hasPeekSlide ? 1 : 0,
      spaceBetween: parseFloat(this.dataset.spaceBetween) || 16,
      loop: this.dataset.loop === 'true',
      // Swiper's own loop-viability check requires roughly
      // ceil(slidesPerView-forced-odd) + max(1, that/2) real slides before
      // it can loop centeredSlides correctly — extra loopAdditionalSlides on
      // top of that doesn't help if the real slide count itself is short of
      // that floor; it only asks Swiper to stretch an even smaller pool of
      // real slides across more clones, which is what caused content to
      // visibly rewrite mid-slide. The section is responsible for rendering
      // enough real slides to clear that floor (see hero-banner.liquid).
      // This is NOT a per-breakpoint option — raising it for a non-centered
      // desktop breakpoint's clone needs also raises it for a centered
      // mobile breakpoint that doesn't need it, and confirmed (twice) to
      // cause a live mobile drag to swap in the wrong slide's image before
      // the swipe even completes: more clones in play gives Swiper's
      // drag-time loopFix() more chances to reassign which DOM slide
      // represents which real content mid-gesture. Desktop's extra
      // requirement is covered by rendering more REAL slide repeats instead
      // (see min_real_slides in the section liquid) rather than more
      // synthetic clones here, so mobile's clone pool stays untouched.
      loopAdditionalSlides: centered ? 0 : hasPeekSlide ? 2 : 0,
      // Without this, slides with height:auto get stretched by flexbox to
      // match the tallest slide in the DOM (e.g. a taller peek/mobile crop),
      // making the whole slider taller than the active image actually is.
      // autoHeight makes the wrapper resize to just the active slide's own height.
      autoHeight: this.dataset.autoHeight === 'true',
      // Lets a slider that starts inside a hidden tab panel (display:none /
      // [hidden]) self-correct its slide widths once that panel becomes
      // visible, instead of being permanently stuck with the zero-width
      // measurements it took at init time.
      observer: true,
      observeParents: true,
      autoplay:
        this.dataset.autoplay === 'true'
          ? { delay: speed, disableOnInteraction: false, pauseOnMouseEnter: true }
          : false,
      navigation: nextEl && prevEl ? { nextEl, prevEl } : false,
      pagination: paginationEl
        ? this.dataset.paginationType === 'progressbar'
          ? { el: paginationEl, type: 'progressbar' }
          : { el: paginationEl, clickable: true }
        : false,
      breakpoints: {
        750: {
          slidesPerView: parseSlidesPerView(this.dataset.slidesTablet, 2.2),
          slidesOffsetBefore:
            (wantsBasePeek && peekOnMobile ? (hasPeekSlide ? offsetMobile : mobilePeek.offset) : 0) +
            edgeOffsetBeforeMobile,
          slidesOffsetAfter: edgeOffsetAfterMobile,
        },
        990: {
          slidesPerView: slidesDesktopSmall || desktopPeek.slidesPerView,
          centeredSlides: centeredDesktop,
          slidesOffsetBefore:
            (wantsDesktopPeek ? (hasPeekSlide ? offsetDesktop : desktopPeek.offset) : 0) + edgeOffsetBeforeDesktop,
          slidesOffsetAfter: edgeOffsetAfterDesktop,
          ...(this.dataset.spaceBetweenDesktop && {
            spaceBetween: parseFloat(this.dataset.spaceBetweenDesktop),
          }),
          ...(Number.isFinite(loopAdditionalSlidesDesktop) && {
            loopAdditionalSlides: loopAdditionalSlidesDesktop,
          }),
        },
        ...(slidesDesktopSmall && {
          1200: {
            slidesPerView: desktopPeek.slidesPerView,
            centeredSlides: centeredDesktop,
            slidesOffsetBefore:
              (wantsDesktopPeek ? (hasPeekSlide ? offsetDesktop : desktopPeek.offset) : 0) + edgeOffsetBeforeDesktop,
            slidesOffsetAfter: edgeOffsetAfterDesktop,
            ...(Number.isFinite(loopAdditionalSlidesDesktop) && {
              loopAdditionalSlides: loopAdditionalSlidesDesktop,
            }),
          },
        }),
      },
    });

    // loopCreate()'s own initial loopFix() call passes direction: "next"
    // whenever centeredSlides is off (see the Swiper source), which only
    // seeds loop clones on the next/right side — nothing sits in the space
    // slidesOffsetBefore reserves on the left until the first manual
    // backward navigation triggers a real loopFix({direction:'prev'}) call.
    // Running that ourselves once, right after construction, seeds the
    // missing prev-side clone immediately instead of waiting on user
    // interaction. Checking the swiper's own resolved params.centeredSlides
    // (rather than our `centered`/`centeredDesktop` locals) matches whichever
    // breakpoint is actually active right now.
    //
    // Do NOT pass slideTo:false here (this was tried and reverted): inside
    // loopFix, slideTo only gates the compensating d.slideTo(idx, 0, false,
    // true) call that re-syncs activeIndex/translate to the DOM after the
    // clone gets prepended — it does NOT gate the prepend itself. Suppressing
    // that resync leaves Swiper's internal position state stale relative to
    // the reordered DOM, which doesn't show up immediately but corrupts the
    // very next real slidePrev()'s grid math, flashing a blank slide for a
    // frame — matching a bug that only ever showed up navigating backward,
    // never forward (forward's loopFix call never overrides slideTo). The
    // resync itself is instant and silent regardless (speed:0,
    // runCallbacks:false internally), so there's no visible cost to leaving
    // slideTo at its default true — it was never needed.
    //
    // Swiper's own setBreakpoint() already calls loopDestroy()+loopCreate()
    // (correctly reseeding both sides) whenever slidesPerView/loop state
    // changes at a breakpoint, so no extra breakpoint/resize/observerUpdate
    // listener is needed here — re-running this manual seed on those events
    // would just re-race the rebuild Swiper already just did correctly.
    if (this.swiper.params.loop && !this.swiper.params.centeredSlides) {
      this.swiper.loopFix({ direction: 'prev' });
    }

    // Opt-in: a section can disable Swiper's own touch/drag swiping (see
    // allowTouchMove above) while still wanting a plain swipe/drag gesture
    // to work — just constrained to always move exactly one slide,
    // regardless of how far or fast the drag was, rather than Swiper's
    // native mid-gesture dragging (which for a high-slidesPerView
    // stacked/peek layout like this one can visibly reassign the wrong
    // slide's image mid-drag — see the loopFix comment on the drag-time
    // reassignment risk). A plain pointerdown/pointerup delta, translated
    // into a single slideNext()/slidePrev() call once the gesture finishes,
    // sidesteps that entirely: nothing moves until the gesture is already
    // complete. Pointer events (rather than touchstart/touchend) cover
    // mouse drag on desktop and pen input the same way as touch, in one
    // listener pair instead of maintaining a separate mouse-only path.
    if (this.dataset.disableSwipe === 'true') {
      const swipeThreshold = 40; // px
      let pointerStartX = null;

      container.addEventListener(
        'pointerdown',
        (event) => {
          pointerStartX = event.clientX;
          // Without capture, pointerup only fires here if the pointer is
          // still over this element when released — a mouse drag that ends
          // outside it (dragged past the edge, released elsewhere) would
          // never fire pointerup on this container at all, leaving
          // pointerStartX stuck until some later, unrelated gesture. Capture
          // routes this pointer's up/move events here regardless of where
          // it physically ends up.
          container.setPointerCapture(event.pointerId);
        },
        { passive: true }
      );

      container.addEventListener(
        'pointerup',
        (event) => {
          if (pointerStartX === null) return;
          const deltaX = event.clientX - pointerStartX;
          pointerStartX = null;
          if (Math.abs(deltaX) < swipeThreshold) return;
          // A second swipe fired while the first is still transitioning can
          // queue a slideNext/slidePrev on top of an in-flight one — for a
          // section with data-sync-video, that's the same play()-then-
          // pause()-before-the-promise-settles race already fixed for
          // updateBeforeMove elsewhere in this file, just triggered by a
          // fast double-swipe instead of a fast double-click. Skipping while
          // animating leaves the in-flight transition to finish on its own;
          // the swipe is simply not registered rather than risking a video
          // stuck paused on whichever slide the race landed on.
          if (this.swiper.animating) return;
          if (deltaX < 0) {
            this.swiper.slideNext();
          } else {
            this.swiper.slidePrev();
          }
        },
        { passive: true }
      );

      // A cancelled gesture (eg. the browser taking over for a system
      // gesture) never fires pointerup — without this, pointerStartX would
      // stay set and the next unrelated pointerdown's delta would be
      // measured from this stale start position instead of its own.
      container.addEventListener(
        'pointercancel',
        () => {
          pointerStartX = null;
        },
        { passive: true }
      );
    }

    // Opt-in "stacked deck" look: slides overlap (via a negative
    // spaceBetween set by the section) instead of sitting side by side, and
    // each one gets a stack-depth-N class for how many positions it sits
    // from the active slide, so CSS can push it progressively further back
    // (lower scale, dimmer, lower z-index) the deeper into the stack it is.
    // Swiper only ever tags the immediate .swiper-slide-prev/-next itself —
    // this fills in the "2 away", "3 away" etc. cases a deeper stack needs.
    // stack-before/stack-after also get added so CSS can anchor each
    // layer's scale to the edge facing the active slide (transform-origin:
    // center would shrink both edges inward evenly, opening a gap between
    // consecutive layers instead of keeping them tucked behind each other).
    if (this.dataset.depthStack === 'true') {
      const maxDepth = parseInt(this.dataset.depthStackLayers, 10) || 3;
      const applyStackDepth = () => {
        this.swiper.slides.forEach((slide) => {
          slide.classList.remove('stack-before', 'stack-after');
          for (let depth = 1; depth <= maxDepth; depth += 1) {
            slide.classList.remove(`stack-depth-${depth}`);
          }
        });
        const { activeIndex, slides } = this.swiper;
        for (let depth = 1; depth <= maxDepth; depth += 1) {
          slides[activeIndex - depth]?.classList.add(`stack-depth-${depth}`, 'stack-before');
          slides[activeIndex + depth]?.classList.add(`stack-depth-${depth}`, 'stack-after');
        }
      };
      this.swiper.on('slideChange', applyStackDepth);
      this.swiper.on('breakpoint', applyStackDepth);
      applyStackDepth();
    }

    // Opt-in: only the active slide's <video> plays, everything else (the
    // rest of the stack, loop clones included) stays paused — otherwise
    // every slide with a muted-autoplay video would play at once just
    // because it's in the DOM, regardless of whether it's ever been the
    // active one. The section is expected to render its videos without the
    // native autoplay attribute so there's no brief window where every
    // slide starts playing before this JS has run.
    if (this.dataset.syncVideo === 'true') {
      // Loop mode's own internal loopFix() can reindex/recycle slides
      // (and therefore fire slideChange) more than once for what's really a
      // single user navigation — eg. wrapping around the loop boundary, or
      // the desktop breakpoint's own prev-side reseed. Each of those calls
      // syncActiveVideo again in quick succession, so a video that just
      // received .play() (promise still pending) can get .pause() called on
      // it before that promise ever settles — browsers treat that as the
      // play() request being interrupted and abort it, silently, since the
      // rejection is swallowed below. The video then never actually starts,
      // even though nothing here looks wrong from the outside. Tracking
      // each video's own in-flight play() promise and waiting for it to
      // settle before pausing removes the interruption entirely, instead of
      // just catching the error it causes.
      const syncActiveVideo = () => {
        const { activeIndex, slides } = this.swiper;
        slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (index === activeIndex) {
            const playPromise = video.play();
            video.__pendingPlay = playPromise ? playPromise.catch(() => {}) : null;

            // autoHeight measures the active slide's height once, right at
            // slideChange — a video's box only reports its real (aspect-
            // ratio-based) height once its metadata has actually loaded, so
            // a newly-recycled loop clone (or any video that hadn't buffered
            // yet at that exact moment) briefly reports the browser's
            // unsized-video default height instead, and Swiper sizes the
            // whole wrapper to that instead of the real video. Re-measuring
            // once metadata does load catches the section back up.
            if (this.dataset.autoHeight === 'true' && video.readyState < 1) {
              video.addEventListener(
                'loadedmetadata',
                () => {
                  if (this.swiper.activeIndex === activeIndex) this.swiper.updateAutoHeight();
                },
                { once: true }
              );
            }
          } else if (video.__pendingPlay) {
            // Capture this specific pending promise: if the video becomes
            // active again (and gets a fresh play() call, replacing
            // video.__pendingPlay) before this one settles, the identity
            // check below skips pausing it out from under that newer call.
            const pending = video.__pendingPlay;
            pending.then(() => {
              if (video.__pendingPlay === pending) video.pause();
            });
          } else {
            video.pause();
          }
        });
      };
      this.swiper.on('slideChange', syncActiveVideo);
      this.swiper.on('breakpoint', syncActiveVideo);
      syncActiveVideo();
    }

    // The "Collection tab" section's tab row can be scrolled/dragged
    // independently of which tab is active (clicking a tab doesn't recenter
    // it), so the active-tab progress indicator below the row can't be
    // positioned by index alone — it has to track the active button's real
    // on-screen position, which shifts as this slider scrolls.
    const progress = this.querySelector('.collection-tab__tabs-progress');
    if (progress) {
      this.syncProgressFill = () => {
        const activeButton = this.querySelector('.collection-tab__tab.is-active');
        if (!activeButton) return;
        const trackRect = progress.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        // Clamped to the track's own width so a button that's scrolled
        // out of view (e.g. clicking the last tab before the carousel has
        // caught up) can never push the fill outside this container.
        const left = Math.min(Math.max(buttonRect.left - trackRect.left, 0), trackRect.width);
        const width = Math.min(buttonRect.width, trackRect.width - left);
        progress.style.setProperty('--active-tab-left', `${left}px`);
        progress.style.setProperty('--active-tab-width', `${width}px`);
      };
      this.swiper.on('progress', this.syncProgressFill);
      this.swiper.on('resize', this.syncProgressFill);
      // 'progress' fires throughout a slideTo animation, but the very last
      // frame's measurement can still land a hair before the transform has
      // actually settled — 'transitionEnd' guarantees one final read against
      // the fully-resolved position instead of a near-there value.
      this.swiper.on('transitionEnd', this.syncProgressFill);
      this.syncProgressFill();
    }
  }

  pause() {
    this.swiper?.autoplay?.stop();
  }

  play() {
    this.swiper?.autoplay?.start();
  }

  isSlideFullyVisible(slideElement) {
    const viewport = this.querySelector('.swiper');
    if (!this.swiper || !viewport) return false;
    const slideRect = slideElement.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    return slideRect.left >= viewportRect.left - 1 && slideRect.right <= viewportRect.right + 1;
  }

  slideToElement(slideElement) {
    if (!this.swiper) return;
    const index = Array.from(slideElement.parentElement.children).indexOf(slideElement);
    if (index === -1) return;
    this.pause();
    if (this.dataset.loop === 'true') {
      this.swiper.slideToLoop(index);
    } else {
      this.swiper.slideTo(index);
    }
  }

  update() {
    this.swiper?.update();
  }
}

customElements.define('swiper-component', SwiperComponent);
